import React, { useState, useEffect } from 'react';
import { BarChart3, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface KPIProps {
  label: string;
  value: string;
  delay: string;
}

const KPI: React.FC<KPIProps> = ({ label, value, delay }) => {
  return (
    <div className="animate-on-scroll text-center" style={{ transitionDelay: delay }}>
      <p className="text-gradient-from text-2xl md:text-3xl font-bold font-heading mb-1">{value}</p>
      <p className="text-text-secondary text-sm">{label}</p>
    </div>
  );
};

const Results: React.FC = () => {
  const { t } = useTranslation();
  const [currentImage, setCurrentImage] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const images = isMobile 
    ? ['/result1mobile.png', '/result2mobile.png']
    : ['/result1.png', '/result2.png'];

  const imageDescriptions = [
    t('results.imageDesc1'),
    t('results.imageDesc2')
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % images.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [images.length]);

  const kpis = [
    { label: t('results.kpi1.label'), value: t('results.kpi1.value') },
    { label: t('results.kpi2.label'), value: t('results.kpi2.value') },
    { label: t('results.kpi3.label'), value: t('results.kpi3.value') },
    { label: t('results.kpi4.label'), value: t('results.kpi4.value') }
  ];

  const nextImage = () => {
    setCurrentImage((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImage((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <section id="results" className="section-spacing bg-bg-secondary relative">
      <div className="container-custom">
        <div className="glass-card p-6 md:p-8 overflow-hidden relative">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center animate-on-scroll">
            {t('results.heading')}
          </h2>
          
          <div className="flex flex-col gap-8">
            <div className="animate-on-scroll">
              <div className="bg-bg-primary/50 rounded-xl p-2 md:p-4 relative group">
                <div className={`relative overflow-hidden w-full ${
                  isMobile ? 'aspect-[9/16]' : 'aspect-[21/9]'
                } max-w-[1200px] mx-auto`}>
                  {images.map((src, index) => (
                    <img
                      key={index}
                      src={src}
                      alt={imageDescriptions[index]}
                      className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-500 ${
                        currentImage === index ? 'opacity-100' : 'opacity-0'
                      }`}
                    />
                  ))}
                </div>

                <button
                  onClick={prevImage}
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-bg-primary/80 p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  aria-label={t('common.previous')}
                >
                  <ChevronLeft className="text-gradient-from" size={24} />
                </button>
                
                <button
                  onClick={nextImage}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-bg-primary/80 p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  aria-label={t('common.next')}
                >
                  <ChevronRight className="text-gradient-from" size={24} />
                </button>

                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                  {images.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImage(index)}
                      className={`w-2 h-2 rounded-full transition-all duration-300 ${
                        currentImage === index ? 'bg-gradient-from w-4' : 'bg-text-secondary/50'
                      }`}
                      aria-label={t('common.viewResult', { number: index + 1 })}
                    />
                  ))}
                </div>
              </div>
              
              <p className="text-text-secondary text-sm italic mt-4 text-center">
                {t('results.disclaimer')}
              </p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {kpis.map((kpi, index) => (
                <KPI
                  key={index}
                  label={kpi.label}
                  value={kpi.value}
                  delay={`${0.1 * (index + 1)}s`}
                />
              ))}
            </div>

            <p className="text-center text-lg text-text-secondary animate-on-scroll">
              {t('results.outperform')}
            </p>
          </div>
          
          {/* Decorative gradient elements */}
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-gradient-from/10 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-gradient-to/10 rounded-full blur-3xl"></div>
        </div>
      </div>
    </section>
  );
};

export default Results;